import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const severityLevels = [
  { value: 'critical', label: 'Critical', description: 'Safety or major functional issue' },
  { value: 'major', label: 'Major', description: 'Significant quality deviation' },
  { value: 'minor', label: 'Minor', description: 'Cosmetic or minor issue' },
];

const defectTypes = [
  { value: 'dimensional', label: 'Dimensional Out of Spec' },
  { value: 'cosmetic', label: 'Cosmetic Defect' },
  { value: 'functional', label: 'Functional Failure' },
  { value: 'material', label: 'Material Defect' },
  { value: 'assembly', label: 'Assembly Error' },
  { value: 'documentation', label: 'Documentation Error' },
  { value: 'contamination', label: 'Contamination' },
  { value: 'other', label: 'Other' },
];

const rootCauses = [
  { value: 'operator_error', label: 'Operator Error' },
  { value: 'equipment_failure', label: 'Equipment Failure' },
  { value: 'material_defect', label: 'Material Defect' },
  { value: 'design_issue', label: 'Design Issue' },
  { value: 'process_issue', label: 'Process Issue' },
  { value: 'supplier_issue', label: 'Supplier Issue' },
  { value: 'training', label: 'Training Gap' },
  { value: 'unknown', label: 'Unknown / TBD' },
];

const dispositions = [
  { value: 'use_as_is', label: 'Use As Is', description: 'Accept with deviation' },
  { value: 'rework', label: 'Rework', description: 'Repair and retest' },
  { value: 'scrap', label: 'Scrap', description: 'Dispose of item' },
  { value: 'return_to_supplier', label: 'Return to Supplier', description: 'RMA to supplier' },
  { value: 'pending', label: 'Pending Review', description: 'Awaiting decision' },
];

const ncrSchema = z.object({
  product_id: z.number({ required_error: 'Please select a product' }),
  serial_number: z.string().optional(),
  work_order_id: z.number().optional(),
  severity: z.enum(['critical', 'major', 'minor'], { required_error: 'Please select severity' }),
  defect_type: z.string().min(1, 'Please select defect type'),
  description: z.string().min(10, 'Please provide a detailed description (min 10 characters)'),
  root_cause: z.string().optional(),
  disposition: z.string().min(1, 'Please select disposition'),
  corrective_action: z.string().optional(),
  reported_by: z.string().min(1, 'Reporter name is required'),
});

type NcrFormData = z.infer<typeof ncrSchema>;

interface Product {
  id: number;
  sku: string;
  name: string;
}

interface WorkOrder {
  id: number;
  wo_number: string;
}

interface NcrReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  products: Product[];
  workOrders: WorkOrder[];
  onSubmit: (data: NcrFormData) => void;
  isLoading?: boolean;
}

export function NcrReportDialog({
  open,
  onOpenChange,
  products,
  workOrders,
  onSubmit,
  isLoading,
}: NcrReportDialogProps) {
  const form = useForm<NcrFormData>({
    resolver: zodResolver(ncrSchema),
    defaultValues: {
      severity: undefined,
      defect_type: '',
      description: '',
      root_cause: '',
      disposition: 'pending',
      corrective_action: '',
      reported_by: '',
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const handleSubmit = (data: NcrFormData) => {
    onSubmit(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create NCR Report</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
          {/* Product Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Product *</Label>
              <Select
                value={form.watch('product_id')?.toString()}
                onValueChange={(value) => form.setValue('product_id', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id.toString()}>
                      <span className="font-mono text-xs mr-2">{product.sku}</span>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.formState.errors.product_id && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.product_id.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Serial Number</Label>
              <Input
                {...form.register('serial_number')}
                placeholder="SN-XXXXX"
                className="font-mono"
              />
            </div>
          </div>

          {/* Work Order */}
          <div className="space-y-2">
            <Label>Related Work Order</Label>
            <Select
              value={form.watch('work_order_id')?.toString() || ''}
              onValueChange={(value) => form.setValue('work_order_id', value ? parseInt(value) : undefined)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select work order (optional)" />
              </SelectTrigger>
              <SelectContent>
                {workOrders.map((wo) => (
                  <SelectItem key={wo.id} value={wo.id.toString()}>
                    {wo.wo_number}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Severity */}
          <div className="space-y-2">
            <Label>Severity *</Label>
            <div className="grid grid-cols-3 gap-2">
              {severityLevels.map((level) => (
                <button
                  key={level.value}
                  type="button"
                  onClick={() => form.setValue('severity', level.value as any)}
                  className={`p-3 rounded-lg border text-left transition-colors ${
                    form.watch('severity') === level.value
                      ? level.value === 'critical'
                        ? 'bg-red-500 text-white border-red-500'
                        : level.value === 'major'
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'bg-yellow-500 text-white border-yellow-500'
                      : 'hover:bg-muted'
                  }`}
                >
                  <p className="font-medium">{level.label}</p>
                  <p className={`text-xs ${form.watch('severity') === level.value ? 'text-white/80' : 'text-muted-foreground'}`}>
                    {level.description}
                  </p>
                </button>
              ))}
            </div>
            {form.formState.errors.severity && (
              <p className="text-sm text-destructive">
                {form.formState.errors.severity.message}
              </p>
            )}
          </div>

          {/* Defect Type */}
          <div className="space-y-2">
            <Label>Defect Type *</Label>
            <Select
              value={form.watch('defect_type')}
              onValueChange={(value) => form.setValue('defect_type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select defect type" />
              </SelectTrigger>
              <SelectContent>
                {defectTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.defect_type && (
              <p className="text-sm text-destructive">
                {form.formState.errors.defect_type.message}
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label>Description *</Label>
            <Textarea
              {...form.register('description')}
              placeholder="Detailed description of the non-conformance..."
              rows={4}
            />
            {form.formState.errors.description && (
              <p className="text-sm text-destructive">
                {form.formState.errors.description.message}
              </p>
            )}
          </div>

          {/* Root Cause */}
          <div className="space-y-2">
            <Label>Root Cause</Label>
            <Select
              value={form.watch('root_cause') || ''}
              onValueChange={(value) => form.setValue('root_cause', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select root cause (if known)" />
              </SelectTrigger>
              <SelectContent>
                {rootCauses.map((cause) => (
                  <SelectItem key={cause.value} value={cause.value}>
                    {cause.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Disposition */}
          <div className="space-y-2">
            <Label>Disposition *</Label>
            <Select
              value={form.watch('disposition')}
              onValueChange={(value) => form.setValue('disposition', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select disposition" />
              </SelectTrigger>
              <SelectContent>
                {dispositions.map((disp) => (
                  <SelectItem key={disp.value} value={disp.value}>
                    <div>
                      <span className="font-medium">{disp.label}</span>
                      <span className="text-muted-foreground ml-2 text-xs">
                        - {disp.description}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.formState.errors.disposition && (
              <p className="text-sm text-destructive">
                {form.formState.errors.disposition.message}
              </p>
            )}
          </div>

          {/* Corrective Action */}
          <div className="space-y-2">
            <Label>Corrective Action</Label>
            <Textarea
              {...form.register('corrective_action')}
              placeholder="Describe corrective actions taken or planned..."
              rows={2}
            />
          </div>

          {/* Reported By */}
          <div className="space-y-2">
            <Label>Reported By *</Label>
            <Input
              {...form.register('reported_by')}
              placeholder="Your name"
            />
            {form.formState.errors.reported_by && (
              <p className="text-sm text-destructive">
                {form.formState.errors.reported_by.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create NCR'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
